import { REFRESH_ACCESS_TOKEN_HEADER } from '@shared/constants';
import axios from 'axios';
import { BASE_API_URL, BASE_URL } from '../config/environment';
import { ReceptionAppScopes } from '../config/interfaces';

export interface LoginInput {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: {
    token: string;
    expiresAt: string;
  };
  refreshToken: {
    token: string;
    expiresAt: string;
  };
  user: {
    id: string;
    email: string;
  };
}

const loginUrl = `${BASE_API_URL}/authenticate`;
const registerUrl = `${BASE_API_URL}/register-account`;
const resetPasswordUrl = `${BASE_API_URL}/reset-password`;
const resetPasswordRequestUrl = `${BASE_API_URL}/send-reset-password-token`;
const redirectUri = BASE_URL + '/reset_password';

export const postLogin = async (data: LoginInput) => {
  const res = await axios.post<LoginResponse>(loginUrl, {
    ...data,
    scope: ReceptionAppScopes.LOGIN,
    grantType: 'password',
  });

  return res?.data;
};

export interface RegisterInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface RegisterResponse {
  account: {
    id: number;
    email: string;
    lastName: string;
    firstName: string;
    identityUserId: string;
    createdAt: string;
    updatedAt: string;
  };
}

export const postRegister = async (
  data: RegisterInput
): Promise<LoginResponse> => {
  await axios.post<LoginResponse>(registerUrl, {
    account: data,
  });

  return await postLogin({ email: data.email, password: data.password });
};

export interface ResetPasswordRequestInput {
  email: string;
}

export interface ResetPasswordRequestResponse {
  ok: string;
}

export const postResetPasswordRequest = async (
  data: ResetPasswordRequestInput
): Promise<ResetPasswordRequestResponse> => {
  const res = await axios.post<ResetPasswordRequestResponse>(
    resetPasswordRequestUrl,
    {
      ...data,
      redirectUri,
    }
  );

  return res.data;
};

export interface ResetPasswordInput {
  email: string;
  token: string;
  newPassword: string;
  newPasswordConfirm?: string;
}

export type ResetPasswordResponse = LoginResponse;

export const postResetPassword = async (
  data: ResetPasswordInput
): Promise<ResetPasswordResponse> => {
  const copyData = { ...data };

  delete copyData.newPasswordConfirm;

  const res = await axios.post<ResetPasswordResponse>(resetPasswordUrl, {
    ...copyData,
    scopes: [ReceptionAppScopes.LOGIN],
  });

  return res.data;
};

export type RefreshAccessTokenResponse = LoginResponse;

export const refreshAccessToken = async (
  refreshToken: string
): Promise<RefreshAccessTokenResponse> => {
  const res = await axios.post<RefreshAccessTokenResponse>(
    `${BASE_API_URL}/authenticate`,
    {
      refreshToken,
      scope: ReceptionAppScopes.LOGIN,
      grantType: 'refresh_token',
    },
    {
      headers: {
        [REFRESH_ACCESS_TOKEN_HEADER]: true,
        Authorization: '',
      },
    }
  );

  return res.data;
};

export const postPasswordLessLogin = async (token: string) => {
  const res = await axios.post<LoginResponse>(loginUrl, {
    passwordlessToken: token,
    scope: ReceptionAppScopes.LOGIN,
    grantType: 'passwordless_token',
  });

  return res?.data;
};
